<template>
  <div
    :class="{ 'active': props.menuOpen }"
    class="mega-menu-mobile fixed top-0 left-0 h-screen w-full overflow-y-auto overscroll-contain px-4 pt-20 py-12 bg-white shadow-lg z-10"
  >
    <!-- Huvudmeny -->
    <div class="-mx-4 bg-workbook-orange text-white px-4 py-6">
      <ul class="flex flex-col gap-4">
        <li v-for="item in mainMenuItems" :key="item.label">
          <template v-if="item.to && isExternal(item.to)">
            <a
              :href="item.to"
              target="_blank"
              rel="noopener"
              class="flex items-center gap-2 text-xl text-white"
              @click="emitClose"
            >
              {{ item.label }}
              <Icon v-if="item.icon" :name="item.icon" />
            </a>
          </template>
          <template v-else>
            <NuxtLink :to="item.to" class="flex items-center gap-2 text-xl text-white" @click="emitClose">
              {{ item.label }}
              <Icon v-if="item.icon" :name="item.icon" />
            </NuxtLink>
          </template>
        </li>
      </ul>
    </div>

    <div class="mt-8 border-t border-black/10 pt-6">
      <h3 class="text-base font-normal">Workbook Demo Menu</h3>
      <p class="text-sm font-normal mt-2">
        Try out the features and intuitive simplicity of the Kudo Sales
        B2B Workbook. Pick some styles and make a test order.
      </p>
    </div>

    <div class="py-6 pl-2 text-lg flex flex-col gap-2">
      <UButton
        v-for="item in menuItems"
        :key="item.key"
        color="neutral"
        variant="ghost"
        class="text-base border-b-2 pb-2 font-normal justify-between"
        :label="item.label"
        trailing-icon="i-lucide-chevron-right"
        @click="openSlideover(item.key)"
      />
    </div>

    <!-- Valutaväxlaren + User dropdown RAD -->
    <div class="flex items-center gap-4 mt-6 mb-2">
      <WorkBookCurrencySelector v-if="auth.isAuthenticated" />
      <UDropdownMenu v-if="auth.isAuthenticated" :items="userDropdownItems" class="user-dropdown">
        <UButton
          size="sm"
          color="neutral"
          variant="ghost"
          trailing-icon="i-lucide-chevron-down"
          aria-label="User menu"
          :label="auth.user?.email"
          class="min-w-[56px] font-medium"
          tabindex="-1"
          @mousedown.prevent
        />
      </UDropdownMenu>
      <UButton v-else size="sm" color="neutral" variant="ghost" icon="workbook:user" aria-label="Login"
        @click="goLoginAndClose">Log in</UButton>
    </div>

    <!-- Första nivå: Cycling / Explore / All / Workbook Navigation -->
    <USlideover
      v-model:open="firstSlideoverOpen"
      :title="firstSlideoverTitle"
      :ui="{ footer: 'justify-end' }"
      :overlay="true"
      side="right"
      class="fixed top-20 left-0 w-full h-[calc(100vh-5rem)] z-40"
      style="max-height:calc(100vh - 5rem);"
    >
      <template #header>
        <div class="headerwrapper">
          <UButton
            color="neutral"
            variant="menuheader"
            size="xl"
            icon="i-lucide-chevron-left"
            :label="firstSlideoverBackLabel"
            @click="closeFirstSlideover"
          />
        </div>
      </template>
      <template #body>
        <div v-if="firstSlideoverKey">
          <div class="bodyheader">{{ firstSlideoverTitle }}</div>
          <div class="bodycontainer">
            <UButton
              v-for="cat in categoryTabs[firstSlideoverKey] || []"
              :key="cat.slug"
              color="neutral"
              variant="submenubutton"
              size="xl"
              :label="cat.label"
              trailing-icon="i-lucide-chevron-right"
              @click="openSecondSlideover(firstSlideoverKey, cat)"
            />
          </div>
        </div>
      </template>
    </USlideover>

    <!-- Andra nivå: kategori → produkter -->
    <USlideover
      v-model:open="secondSlideoverOpen"
      :title="secondSlideoverTitle"
      :ui="{ footer: 'justify-end' }"
      :overlay="true"
      side="right"
      class="fixed top-20 left-0 w-full h-[calc(100vh-5rem)] z-50"
      style="max-height:calc(100vh - 5rem);"
    >
      <template #header>
        <div class="headerwrapper">
          <UButton
            color="neutral"
            variant="menuheader"
            size="xl"
            icon="i-lucide-chevron-left"
            class="headerbutton"
            :label="secondSlideoverBackLabel"
            @click="closeSecondSlideover"
          />
        </div>
      </template>
      <template #body>
        <div v-if="secondSlideoverCategory">
          <div class="bodyheader">{{ secondSlideoverTitle }}</div>
          <div class="bodycontainer">
            <!-- See all link -->
            <NuxtLink
              :to="`/products/${secondSlideoverCategory.slug}${firstSlideoverKey !== 'All' ? '?category=' + encodeURIComponent(firstSlideoverKey) : ''}`"
              class="flex flex-col w-full"
              @click="emitClose"
            >
              <UButton
                color="neutral"
                variant="submenubutton"
                size="xl"
                :label="firstSlideoverKey === 'All'
                  ? `See all ${secondSlideoverCategory.label}`
                  : `See all ${firstSlideoverTitle} ${secondSlideoverCategory.label} →`"
                trailing-icon="i-lucide-chevron-right"
              />
            </NuxtLink>
            <!-- M E N S -->
            <div v-if="hasBothGenders(secondSlideoverCategory)">
              <div class="font-semibold mt-6 mb-2 text-base">MEN’S</div>
              <div v-for="prod in secondSlideoverCategory.products.Male" :key="prod.Style">
                <NuxtLink
                  :to="`/products/${secondSlideoverCategory.slug}${firstSlideoverKey !== 'All' ? '?category=' + encodeURIComponent(firstSlideoverKey) : ''}#style-${prod.Style}`"
                  class="block py-3 font-normal"
                  @click="emitClose"
                >
                  {{ prod.name }}
                </NuxtLink>
              </div>
              <!-- W O M E N S-->
              <div class="font-semibold mt-6 mb-2 text-base">WOMEN’S</div>
              <div v-for="prod in secondSlideoverCategory.products.Female" :key="prod.Style">
                <NuxtLink
                  :to="`/products/${secondSlideoverCategory.slug}${firstSlideoverKey !== 'All' ? '?category=' + encodeURIComponent(firstSlideoverKey) : ''}#style-${prod.Style}`"
                  class="block py-3 font-normal"
                  @click="emitClose"
                >
                  {{ prod.name }}
                </NuxtLink>
              </div>
            </div>
            <!-- U N I S E X / A L L-->
            <div v-else>
              <div v-if="hasSingleGender(secondSlideoverCategory)" class="font-semibold mt-6 mb-2 text-base">
                {{ singleGenderLabel(secondSlideoverCategory) }}
              </div>
              <div v-for="prod in secondSlideoverCategory.products.All" :key="prod.Style">
                <NuxtLink
                  :to="`/products/${secondSlideoverCategory.slug}${firstSlideoverKey !== 'All' ? '?category=' + encodeURIComponent(firstSlideoverKey) : ''}#style-${prod.Style}`"
                  class="block py-3 font-normal"
                  @click="emitClose"
                >
                  {{ prod.name }}
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </template>
    </USlideover>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useCatalogStore } from '@/store/catalog'
import { useAuthStore } from '@/store/auth'
import WorkBookCurrencySelector from '@/components/workbook/CurrencySelector.vue'

const props = defineProps<{ menuOpen: boolean }>()
const emit = defineEmits(['close'])

const auth = useAuthStore()

const isDev = import.meta.dev

const catalog = useCatalogStore()
const menuItems = computed(() => {
  const items = [{ key: 'All', label: 'All' }]
  for (const cat of catalog.topCategories) {
    items.push({ key: cat, label: cat })
  }
  return items
})

function buildCategoryTabs(cats) {
  return cats.map(cat => {
    const male = cat.ProductModels.filter(m => m.Gender === 'Male')
    const female = cat.ProductModels.filter(m => m.Gender === 'Female')
    const all = cat.ProductModels.filter(m => !m.Gender || (m.Gender !== 'Male' && m.Gender !== 'Female'))

    let allProducts = []
    if (male.length && !female.length) allProducts = male
    else if (female.length && !male.length) allProducts = female
    else if (all.length) allProducts = all

    return {
      label: cat.Name,
      slug: cat.Slug,
      products: {
        Male: male,
        Female: female,
        All: allProducts
      }
    }
  })
}

const categoryTabs = computed<Record<string, ReturnType<typeof buildCategoryTabs>>>(() => {
  const tabs: Record<string, ReturnType<typeof buildCategoryTabs>> = {
    All: buildCategoryTabs(catalog.categories)
  }
  for (const topCat of catalog.topCategories) {
    tabs[topCat] = buildCategoryTabs(catalog.categoriesByTopCategory(topCat))
  }
  return tabs
})

function hasBothGenders(catItem) {
  return catItem.products.Male.length && catItem.products.Female.length
}
function hasSingleGender(catItem) {
  return (
    (catItem.products.Male.length && !catItem.products.Female.length) ||
    (!catItem.products.Male.length && catItem.products.Female.length)
  )
}
function singleGenderLabel(catItem) {
  if (catItem.products.Male.length && !catItem.products.Female.length) return "MEN'S"
  if (catItem.products.Female.length && !catItem.products.Male.length) return "WOMEN'S"
  return ""
}
function isExternal(to) {
  return typeof to === 'string' && /^https?:\/\//.test(to)
}

// Hämta mainnav från Nuxt Content
const { data: mainnav } = await useAsyncData('mainnav', () =>
  queryCollection('mainnav').first()
)
const mainMenuItems = computed(() => {
  const menu = Array.isArray(mainnav.value?.menuitems) ? mainnav.value.menuitems : []
  return menu.filter(item => item && typeof item.label === 'string' && item.label.length);
})

// User dropdown logic
async function logoutAndClose() {
  await useNuxtApp().$logoutFirebase()
  auth.clearUser()
  emitClose()
  navigateTo('/')
}
function goLoginAndClose() {
  emitClose()
  navigateTo('/login')
}
const userDropdownItems = computed(() => {
  const items = []
  
  if (auth.isAdmin || auth.isRep || auth.isRepAdmin) {
    items.push({
      label: 'Manage Orders',
      onSelect: () => {
        emitClose()
        navigateTo('/admin-orders')
      }
    })
    
    if (isDev) {
      items.push({
        label: 'Manage Dealers',
        onSelect: () => {
          emitClose()
          navigateTo('/admin-dealers')
        }
      })
      items.push({
        label: 'Manage Users',
        onSelect: () => {
          emitClose()
          navigateTo('/admin-users')
        }
      })
    }
  }
  
  if ((auth.isAdmin || auth.isRepAdmin) && isDev) {
    items.push({
      label: 'Manage Reps',
      onSelect: () => {
        emitClose()
        navigateTo('/admin-reps')
      }
    })
  }
  
  items.push({
    label: 'My Orders',
    onSelect: () => {
      emitClose()
      navigateTo('/orders')
    }
  })
  
  items.push({
    label: 'Saved Carts',
    onSelect: () => {
      emitClose()
      navigateTo('/saved-carts')
    }
  })
  
  items.push({
    label: 'Log Out',
    onSelect: logoutAndClose
  })
  
  return items
})

const firstSlideoverOpen = ref(false)
const firstSlideoverKey = ref('')
const firstSlideoverTitle = computed(() => {
  return menuItems.value.find(i => i.key === firstSlideoverKey.value)?.label || ''
})
const firstSlideoverBackLabel = computed(() => 'Menu')

const secondSlideoverOpen = ref(false)
const secondSlideoverCategory = ref<any>(null)
const secondSlideoverTitle = computed(() => secondSlideoverCategory.value?.label || '')
const secondSlideoverBackLabel = computed(() => firstSlideoverTitle.value)

function openSlideover(key) {
  firstSlideoverKey.value = key
  firstSlideoverOpen.value = true
  secondSlideoverOpen.value = false
  secondSlideoverCategory.value = null
}
function closeFirstSlideover() {
  firstSlideoverOpen.value = false
  secondSlideoverOpen.value = false
  secondSlideoverCategory.value = null
}
function openSecondSlideover(type, cat) {
  secondSlideoverCategory.value = cat
  secondSlideoverOpen.value = true
}
function closeSecondSlideover() {
  secondSlideoverOpen.value = false
  secondSlideoverCategory.value = null
}

function emitClose() {
  emit('close')
  firstSlideoverOpen.value = false
  secondSlideoverOpen.value = false
  firstSlideoverKey.value = ''
  secondSlideoverCategory.value = null
}

watch(() => props.menuOpen, (val) => {
  if (!val) {
    firstSlideoverOpen.value = false
    secondSlideoverOpen.value = false
    firstSlideoverKey.value = ''
    secondSlideoverCategory.value = null
  }
})
</script>

<style scoped>
@reference "../../assets/css/main.css";
.mega-menu-mobile {
  transition: all 0.3s var(--ease-snappy);
  transform: translateY(-100%);
  opacity: 0;
}
.mega-menu-mobile.active {
  pointer-events: all;
  opacity: 1;
  transform: translateY(0%);
}

.headerwrapper{
  @apply border-b-2 pb-2 w-full flex items-center gap-2;
}

.bodyheader{
  @apply border-b-2 pb-4 pl-12 font-bold text-base;
}

.bodycontainer{
  @apply flex flex-col pl-12;
}
</style>
